import React from 'react';

/**
 * Diligentsia × Morsebridge co-branded partnership landing page.
 *
 * Opened in a new browser tab from the Financial / Technology tabs on the company, advisor and
 * investor dashboards. Renders standalone — see STANDALONE_ROUTES in constants/routes.js — because
 * it ships its own header and footer rather than the site chrome.
 */

const KOALENDAR = 'https://koalendar.com/e/meet-with-mark-bernstein';

const diligentsiaIcon = require('../../images/morsebridge-assets/diligentsia-icon.png');
const morsebridgeLogo = require('../../images/morsebridge-assets/morsebridge-logo.png');
const morsebridgeIcon = require('../../images/morsebridge-assets/morsebridge-icon.png');
const heroGraphic = require('../../images/morsebridge-assets/morsebridge-hero.png');

const RESOURCES = [
    {
        label: 'RESOURCE',
        title: 'Sample IC memo',
        desc: 'An anonymized investment committee memo produced from a live deal. Shows exactly what you receive and to what standard.',
        cta: 'Download PDF',
    },
    {
        label: 'RESOURCE',
        title: 'AI deal-sourcing guide',
        desc: 'How we build proprietary origination pipelines across web, filings and trade sources, and verify every signal before it reaches you.',
        cta: 'Download PDF',
    },
    {
        label: 'TOOL',
        title: 'LP Scouting Tool',
        desc: 'A basic version, free for PE and VC firms. Identify and shortlist limited partners that fit your fund profile, then take the shortlist straight into outreach.',
        cta: 'Open Tool',
    },
];

// Cards 1–6 are Morsebridge-attributed, 7–9 Diligentsia-attributed.
const SERVICES = [
    { brand: 'mb', title: 'Deal sourcing', desc: 'Proprietary origination and target identification, verified across multiple sources.' },
    { brand: 'mb', title: 'CIM and SIM screening', desc: 'Fast first-view screening of inbound opportunities with a clear go or pass read.' },
    { brand: 'mb', title: 'Commercial due diligence', desc: 'Market, customer and competitive assessment to test the investment thesis.' },
    { brand: 'mb', title: 'Financial due diligence and QoE', desc: 'Quality of earnings, addback validation and an adjusted EBITDA you can defend.' },
    { brand: 'mb', title: 'Management due diligence', desc: 'Owner dependence, team capability and key-person risk assessment.' },
    { brand: 'mb', title: 'IC memo preparation', desc: 'Decision-ready investment committee memos built from the diligence record.' },
    { brand: 'dg', title: 'Investment readiness and diligence packs', desc: 'Diligence-pack creation and reverse vendor diligence, financial and technical.' },
    { brand: 'dg', title: 'Portfolio monitoring', desc: 'Post-investment reporting roll-ups and performance monitoring across the portfolio.' },
    { brand: 'dg', title: 'Exit preparation', desc: 'Exit-readiness assessment and vendor diligence, viewed the way a buyer will.' },
];

const Lockup = ({ small }) => (
    <div className={`mb-lockup${small ? ' mb-lockup--sm' : ''}`}>
        <img className="mb-lockup-mark" src={diligentsiaIcon} alt="Diligentsia" />
        <span className="mb-lockup-word">Diligentsia</span>
        <span className="mb-lockup-x">×</span>
        <img className="mb-lockup-mb" src={morsebridgeLogo} alt="Morsebridge" />
    </div>
);

const MorseBridge = () => (
    <div className="mb-page">
        {/* ── Header ── */}
        <header className="mb-header">
            <Lockup />
            <a className="mb-btn mb-btn--outline" href={KOALENDAR} target="_blank" rel="noopener noreferrer">Book A Call</a>
        </header>

        {/* ── Hero ── */}
        <section className="mb-hero">
            <div className="mb-hero-copy">
                <p className="mb-eyebrow">Happy to announce our partnership with Morsebridge, the agent layer for private equity deal teams</p>
                <h1>One Partner For The Full PE Deal Journey</h1>
                <p className="mb-body">Sourcing, diligence, monitoring and exit preparation, built into a stack that fits how your firm works.</p>
                <p className="mb-body mb-body--strong">Morsebridge Ventures will provide support to private equity firms and portfolio companies for deal flow automation.</p>
                <p className="mb-body">Morsebridge builds a custom AI stack around your deal process. We give you the tools and workflows to run sourcing in-house, configured to your firm and supported by qualified advisors. Diligentsia provides investment readiness and diligence services to the target company.</p>
            </div>
            <div className="mb-hero-card">
                <img src={heroGraphic} alt="Diligentsia and Morsebridge partnership" />
            </div>
        </section>

        {/* ── Free Resources & Tools ── */}
        <section className="mb-dark">
            <div className="mb-dark-inner">
                <h2>Free Resources &amp; Tools</h2>
                <p className="mb-sub">Three resources showing how we source, screen and diligence deals.</p>
                <div className="mb-res-grid">
                    {RESOURCES.map(r => (
                        <div className="mb-res-card" key={r.title}>
                            <span className="mb-res-label">{r.label}</span>
                            <h3>{r.title}</h3>
                            <p>{r.desc}</p>
                            <a className="mb-btn mb-btn--solid" rel="noopener noreferrer">{r.cta}</a>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── Combined Services ── */}
        <section className="mb-services">
            <h2>Combined Services</h2>
            <p className="mb-services-intro">Each engagement is scoped to your firm. We build the tools and workflows into your stack, configured to how you source, diligence and monitor.</p>
            <div className="mb-svc-grid">
                {SERVICES.map(s => (
                    <div className={`mb-svc-card mb-svc-card--${s.brand}`} key={s.title}>
                        <div className="mb-svc-head">
                            <img className="mb-svc-icon" src={s.brand === 'mb' ? morsebridgeIcon : diligentsiaIcon} alt="" aria-hidden="true" />
                            <div>
                                <span className="mb-svc-brand">{s.brand === 'mb' ? 'MORESEBRIDGE' : 'DILIGENTSIA'}</span>
                                <h4>{s.title}</h4>
                            </div>
                        </div>
                        <p>{s.desc}</p>
                    </div>
                ))}
            </div>
        </section>

        {/* ── Closing CTA ── */}
        <section className="mb-cta" id="book" style={{ marginBottom: '0px' }}>
            <div className="mb-cta-inner">
                <h2>Talk to us about your next deal</h2>
                <p>Tell us where you are in the process. We will tell you what we can deliver, the scope and the timeline.</p>
                <a className="mb-btn mb-btn--cta" href={KOALENDAR} target="_blank" rel="noopener noreferrer">Book A Call</a>
            </div>
        </section>

        {/* ── Footer ── */}
        <footer className="mb-footer">
            <Lockup small />
            <p className="mb-footer-note">A DILIGENTSIA AND MORSEBRIDGE<br />PARTNERSHIP</p>
        </footer>
    </div>
);

export default MorseBridge;
